import { useTheme } from '@placer-ui/components';
import { useCallback, useEffect, useMemo } from 'react';
import { ColorVariant } from '@placer-ui/components/dist/foundations/theme/theme-types';
import { getFeatureFlags } from 'core/flow-control';
import { usePlatformConfiguration } from 'features/settings/store/selectors';
import { PLACE_COLORS } from 'core/constants/place-colors';
import { store } from 'store/store';
import { ChainEntityModelMapper } from 'features/chains/common/util/chain-placer-entity-model-mapper';
import { ChainEntityModelMapper as IndustryEntityModelMapper } from 'features/industry/common/util/chain-placer-entity-model-mapper';
import { setReportChainsAtChains } from 'features/chains/store/chains-report-chains-store';
import { setReportChainsAtChains as setReportIndustriesAtChains } from 'features/industry/store/chains-report-chains-store';
import { Chain, PlacerEntityModel, Tag } from '@placer-ui/types';
import { setPOIs } from 'features/insights/store/poi/poi-actions';
import { batch } from 'react-redux';
import { Venue } from 'core/entities/venue/venueAdmin';
import { VenueFilter } from 'core/entities/venue/venueAdminFilter';

const usePoiColors = () => {
    const { activeTheme } = useTheme();

    const poiColors = useMemo(() => activeTheme?.theme?.POI, [activeTheme?.theme?.POI]);
    const getPoisColorsByVariant = useCallback(
        (variant: keyof ColorVariant = 'default'): string[] =>
            Object.entries(poiColors).map(([key, value]) => value[variant]!),
        [poiColors],
    );
    const getPoiColorByIndex = useCallback(
        (index: number, variant: keyof ColorVariant = 'default') => {
            return poiColors?.[index]?.[variant] ?? '';
        },
        [poiColors],
    );

    return {
        poiColors,
        getPoisColorsByVariant,
        getPoiColorByIndex,
    };
};

const updateReportsPoisColors = () => {
    // Update chains colors
    const updatedChains = Object.values(store.getState().chains.entities).reduce<
        PlacerEntityModel<Chain>[]
    >?.((acc, chain, i) => {
        if (chain) {
            const newChain = ChainEntityModelMapper.createEntityModel(chain, PLACE_COLORS.main[i]);
            acc.push(newChain);
        }
        return acc;
    }, []);

    // Update industries colors
    const updatedIndustries = Object.values(store.getState().industry.entities).reduce<
        PlacerEntityModel<Tag>[]
    >?.((acc, industry, i) => {
        if (industry) {
            const newIndustry = IndustryEntityModelMapper.createEntityModel(
                industry,
                PLACE_COLORS.main[i],
            );
            acc.push(newIndustry);
        }
        return acc;
    }, []);

    // Update property POIs colors
    const currentStoreEntities = store.getState().poi.entities;
    const currentEntities = Object.values(currentStoreEntities);
    const updatedEntities = currentEntities.reduce<Venue[]>((acc, entity, i) => {
        if (entity?.originalEntity) {
            const venue = new Venue(entity.originalEntity.originalShape, currentStoreEntities);

            // Preserve all existing data
            venue.customData = { ...entity.originalEntity.customData };

            venue.setFilter(
                new VenueFilter({
                    ...entity.filter,
                    attributes: entity.filter.attributes,
                }),
            );

            venue.setColor(PLACE_COLORS.main[i], PLACE_COLORS.lighter[i], PLACE_COLORS.darker[i]);
            acc.push(venue);
        }
        return acc;
    }, []);

    batch(() => {
        if (updatedChains?.length) {
            store.dispatch(setReportChainsAtChains(updatedChains));
        }
        if (updatedIndustries?.length) {
            store.dispatch(setReportIndustriesAtChains(updatedIndustries));
        }
        if (updatedEntities?.length) {
            // @ts-ignore
            store.dispatch(setPOIs(updatedEntities));
        }
    });
};

export const useInitPoiThemeColor = () => {
    const { enable_accessibility_mode_settings_ff } = getFeatureFlags();
    const { setThemes, activeTheme } = useTheme();
    const { getPoisColorsByVariant } = usePoiColors();
    const platformValues = usePlatformConfiguration();

    useEffect(() => {
        if (enable_accessibility_mode_settings_ff) {
            PLACE_COLORS.main = getPoisColorsByVariant('default');
            PLACE_COLORS.darker = getPoisColorsByVariant('dark');
            PLACE_COLORS.lighter = getPoisColorsByVariant('light');

            updateReportsPoisColors();
        }
    }, [enable_accessibility_mode_settings_ff, getPoisColorsByVariant]);

    useEffect(() => {
        const theme = platformValues?.platform_config_colors_accessible_mode
            ? 'light-a11y'
            : 'light';
        if (!!enable_accessibility_mode_settings_ff && theme !== activeTheme?.themeMode) {
            setThemes(theme);
        }
    }, [
        activeTheme?.themeMode,
        enable_accessibility_mode_settings_ff,
        platformValues?.platform_config_colors_accessible_mode,
        setThemes,
    ]);
};
